import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { StatusBadge, RequestDetailModal } from '../components/RequestDetail';
import Avatar from '../components/Avatar';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import Sparkline from '../components/Sparkline';
import MyPerformance from '../components/MyPerformance';
import { ArrowRight, Briefcase, DollarSign, Flame, MapPin, Sparkles, TrendingUp, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const HIGH_VALUE_THRESHOLD = 10000;

const DeltaLabel = ({ delta, prefix = '', suffix = '' }) => {
  if (delta == null) return null;
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  const abs = Math.abs(delta);
  const formatted = abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k` : abs.toLocaleString();
  const color = isPositive ? 'text-green-400' : isNegative ? 'text-red-400' : 'text-zinc-600';
  const sign = isPositive ? '+' : isNegative ? '-' : '';
  const label = delta === 0 ? 'same as last mo' : `${sign}${prefix}${formatted}${suffix} vs last mo`;

  return (
    <span className={`text-[9px] font-medium ${color} whitespace-nowrap`} data-testid="mom-delta">
      {label}
    </span>
  );
};

const SalespersonDashboard = () => {
  const { user, logout, token } = useAuth();
  const [feed, setFeed] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetailOpen, setRequestDetailOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [proposedTerms, setProposedTerms] = useState({ commissionPct: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [myStats, setMyStats] = useState(null);
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    fetchFeed();
    fetchMyRequests();
    fetchMyStats();
    fetchTrend();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await axios.get(`${API}/salesperson/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeed(response.data);
    } catch (error) {
      toast.error('Failed to fetch feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
    }
  };

  const fetchMyStats = async () => {
    try {
      const response = await axios.get(`${API}/salesperson/my-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyStats(response.data);
    } catch {
      // silent — stats bar just won't show
    }
  };

  const fetchTrend = async () => {
    try {
      const response = await axios.get(`${API}/salesperson/my-trend`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrend(response.data);
    } catch {
      // silent
    }
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setRequestDetailOpen(true);
  };

  const openRequestModal = (post) => {
    setSelectedPost(post);
    if (post.defaultTerms) {
      setProposedTerms({
        commissionPct: post.defaultTerms.commissionPct.toString(),
        notes: post.defaultTerms.notes || ''
      });
    } else {
      setProposedTerms({ commissionPct: '', notes: '' });
    }
    setRequestModalOpen(true);
  };

  const handleSubmitRequest = async () => {
    try {
      setSubmitting(true);
      const payload = { post_id: selectedPost.id };
      if (proposedTerms.commissionPct !== '') {
        const pct = Number(proposedTerms.commissionPct);
        if (Number.isNaN(pct) || pct < 0 || pct > 60) {
          toast.error('Commission must be between 0 and 60%');
          return;
        }
        payload.proposedTerms = {
          commissionPct: pct,
          notes: proposedTerms.notes || null,
          status: 'PENDING'
        };
      }
      await axios.post(`${API}/salesperson/requests`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request sent successfully!');
      setRequestModalOpen(false);
      fetchFeed();
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatLocation = (loc) => [loc.city, loc.region, loc.country].filter(Boolean).join(', ');
  const isAlreadyRequested = (postId) => myRequests.some(req => req.post_id === postId);

  const isHighValue = (post) => post.price >= HIGH_VALUE_THRESHOLD;

  // Sort feed: high-value first
  const sortedFeed = [...feed].sort((a, b) => (b.price || 0) - (a.price || 0));
  const featuredPost = sortedFeed[0];
  const remainingFeed = sortedFeed.slice(1);

  const activeRequests = myRequests.filter(r => r.status === 'APPROVED' || r.status === 'REQUESTED');
  const newOpportunities = feed.filter(p => !isAlreadyRequested(p.id)).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg transition-colors">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="bg-brand-surface/80 backdrop-blur-md border-b border-zinc-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/logo.svg" alt="Sale Sphere" className="h-7 w-7" />
              <div>
                <h1 className="font-display text-xl text-brand-text tracking-wide">Sale Sphere</h1>
                <p className="text-sm text-brand-muted">Salesperson Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <NotificationBell />
              <Link to="/settings/profile" className="hover:opacity-80 transition-opacity flex items-center space-x-2">
                <Avatar user={user} size="sm" />
                <div className="text-sm text-brand-text hidden sm:block">
                  {user?.name}
                  {user?.city && (
                    <div className="text-xs text-brand-muted">
                      {[user.city, user.region, user.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </Link>
              <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Quick Stats */}
        {myStats && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="quick-stats-bar">
            <div className="rounded-xl border border-zinc-800 bg-brand-surface px-4 py-3.5 flex items-center justify-between" data-testid="stat-deals-won">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <div className="text-xl font-bold text-brand-text leading-none">{myStats.deals_won}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Deals Won</div>
                </div>
              </div>
              {trend && (
                <div className="flex flex-col items-end gap-1">
                  <Sparkline data={trend.deals} width={80} height={24} />
                  <DeltaLabel delta={trend.mom?.deals_delta} suffix={trend.mom?.deals_delta === 1 || trend.mom?.deals_delta === -1 ? ' deal' : ' deals'} />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-zinc-800 bg-brand-surface px-4 py-3.5 flex items-center justify-between" data-testid="stat-revenue">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4 text-brand-gold" />
                </div>
                <div>
                  <div className="text-xl font-bold text-brand-text leading-none">
                    ${myStats.total_revenue >= 1000 ? `${(myStats.total_revenue / 1000).toFixed(myStats.total_revenue >= 10000 ? 0 : 1)}k` : myStats.total_revenue.toLocaleString()}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Revenue</div>
                </div>
              </div>
              {trend && (
                <div className="flex flex-col items-end gap-1">
                  <Sparkline data={trend.revenue} width={80} height={24} />
                  <DeltaLabel delta={trend.mom?.revenue_delta} prefix="$" />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-zinc-800 bg-brand-surface px-4 py-3.5 flex items-center gap-3" data-testid="stat-xp">
              <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-brand-text leading-none">{myStats.xp.toLocaleString()}</span>
                  {myStats.streak > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded-md border border-orange-400/20" data-testid="stat-streak">
                      <Flame className="w-2.5 h-2.5" />{myStats.streak}mo
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">XP Points</div>
              </div>
            </div>
          </div>
        )}

        {/* Momentum Indicators */}
        {(activeRequests.length > 0 || newOpportunities > 0) && (
          <div className="flex flex-wrap gap-3 mb-6" data-testid="momentum-indicators">
            {activeRequests.length > 0 && (
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-green-500/8 border border-green-500/15 text-green-400 text-xs font-medium">
                <Briefcase className="w-3.5 h-3.5" />
                {activeRequests.length} active request{activeRequests.length !== 1 ? 's' : ''}
              </div>
            )}
            {newOpportunities > 0 && (
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-brand-gold/8 border border-brand-gold/15 text-brand-gold text-xs font-medium">
                <Sparkles className="w-3.5 h-3.5" />
                {newOpportunities} new opportunit{newOpportunities !== 1 ? 'ies' : 'y'} available
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="feed" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="feed" data-testid="feed-tab">Available Posts</TabsTrigger>
            <TabsTrigger value="requests" data-testid="requests-tab">My Requests</TabsTrigger>
            <TabsTrigger value="performance" data-testid="performance-tab">My Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="feed">
            {feed.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-brand-surface p-12 text-center">
                <p className="text-brand-muted">No posts available for your location at the moment.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Featured Opportunity */}
                {featuredPost && (
                  <div
                    data-testid="featured-opportunity"
                    className={`relative rounded-2xl border border-brand-gold/20 bg-gradient-to-br from-brand-surface via-brand-surface to-brand-gold/[0.04] p-6 sm:p-8 overflow-hidden group ${
                      isAlreadyRequested(featuredPost.id) ? 'cursor-default' : 'cursor-pointer'
                    }`}
                    onClick={() => !isAlreadyRequested(featuredPost.id) && openRequestModal(featuredPost)}
                  >
                    {/* Subtle accent line */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent" />

                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-semibold tracking-widest uppercase text-brand-gold bg-brand-gold/10 px-2 py-0.5 rounded-md border border-brand-gold/20">
                            Featured Opportunity
                          </span>
                          {isHighValue(featuredPost) && (
                            <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-md border border-amber-400/20">
                              High Value
                            </span>
                          )}
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-brand-text mb-2 group-hover:text-brand-gold transition-colors">
                          {featuredPost.title}
                        </h2>
                        <p className="text-sm text-brand-muted leading-relaxed mb-4 line-clamp-2">
                          {featuredPost.description}
                        </p>
                        {featuredPost.include_locations?.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-zinc-500 mb-4">
                            <MapPin className="w-3 h-3" />
                            {featuredPost.include_locations.map(formatLocation).join('; ')}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className="text-3xl sm:text-4xl font-bold text-brand-text mb-1">
                          ${featuredPost.price?.toLocaleString()}
                        </div>
                        <div className="text-xs text-brand-muted mb-4">deal value</div>
                        {isAlreadyRequested(featuredPost.id) ? (
                          <div className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-400 text-sm font-medium">
                            Already Requested
                          </div>
                        ) : (
                          <button
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-gold hover:bg-brand-gold600 text-black text-sm font-bold shadow-[0_2px_16px_rgba(212,175,55,0.3)] hover:shadow-[0_4px_24px_rgba(212,175,55,0.4)] transition-all"
                            data-testid="featured-start-selling"
                            onClick={(e) => { e.stopPropagation(); openRequestModal(featuredPost); }}
                          >
                            Start Selling <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Post Grid */}
                <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {remainingFeed.map((post) => {
                    const highValue = isHighValue(post);
                    const requested = isAlreadyRequested(post.id);
                    return (
                      <div
                        key={post.id}
                        data-testid="feed-post-card"
                        className={`group rounded-xl border p-5 transition-all duration-200 ${
                          highValue
                            ? 'border-brand-gold/15 bg-gradient-to-b from-brand-surface to-brand-gold/[0.02] hover:border-brand-gold/30'
                            : 'border-zinc-800 bg-brand-surface hover:border-zinc-700'
                        }`}
                      >
                        {/* Top row: title + value badge */}
                        <div className="flex justify-between items-start gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            {highValue && (
                              <div className="flex items-center gap-1 mb-1.5">
                                <TrendingUp className="w-3 h-3 text-amber-400" />
                                <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">High Commission</span>
                              </div>
                            )}
                            <h3 className="text-base font-semibold text-brand-text leading-snug group-hover:text-brand-gold transition-colors">
                              {post.title}
                            </h3>
                            <span className="text-xs text-brand-muted mt-0.5 block">{post.category}</span>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className={`text-lg font-bold ${highValue ? 'text-brand-gold' : 'text-brand-text'}`}>
                              ${post.price?.toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-zinc-400 leading-relaxed mb-3 line-clamp-2">
                          {post.description}
                        </p>

                        {/* Meta */}
                        <div className="space-y-1.5 text-xs text-zinc-500 mb-4">
                          {post.rules_of_engagement && (
                            <div className="line-clamp-1">
                              <span className="text-zinc-400 font-medium">Rules:</span> {post.rules_of_engagement}
                            </div>
                          )}
                          {post.include_locations?.length > 0 && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{post.include_locations.map(formatLocation).join('; ')}</span>
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <button
                          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
                            requested
                              ? 'bg-zinc-800 text-zinc-500 cursor-default'
                              : 'bg-brand-gold hover:bg-brand-gold600 text-black shadow-[0_1px_8px_rgba(212,175,55,0.2)] hover:shadow-[0_2px_16px_rgba(212,175,55,0.35)]'
                          }`}
                          onClick={() => !requested && openRequestModal(post)}
                          disabled={requested}
                          data-testid="request-to-sell-button"
                        >
                          {requested ? 'Already Requested' : 'Start Selling'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests">
            {myRequests.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 bg-brand-surface p-12 text-center">
                <p className="text-brand-muted">You haven't requested any posts yet.</p>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {myRequests.map((request) => (
                  <div
                    key={request.id}
                    data-testid="my-request-card"
                    onClick={() => openRequestDetail(request)}
                    className="group rounded-xl border border-zinc-800 bg-brand-surface p-5 cursor-pointer hover:border-zinc-700 transition-all"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-brand-text group-hover:text-brand-gold transition-colors">
                          {request.post?.title || 'Post'}
                        </h3>
                        <span className="text-xs text-brand-muted">{request.post?.category}</span>
                      </div>
                      <StatusBadge status={request.status} />
                    </div>
                    {request.post && (
                      <>
                        <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{request.post.description}</p>
                        <div className="flex items-center justify-between text-xs text-zinc-500">
                          <span>${request.post.price?.toLocaleString()}</span>
                          <span>{new Date(request.created_at).toLocaleDateString()}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="performance">
            <MyPerformance token={token} />
          </TabsContent>
        </Tabs>
      </main>

      <RequestDetailModal
        request={selectedRequest}
        open={requestDetailOpen}
        onClose={() => setRequestDetailOpen(false)}
        token={token}
        userRole="SALESPERSON"
        onUpdate={fetchMyRequests}
      />

      {/* Start Selling Modal */}
      <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
        <DialogContent
          className="max-w-2xl bg-[#0F1115] border border-white/[0.08] rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.7),0_0_0_1px_rgba(212,175,55,0.06)] p-0 gap-0 overflow-hidden [&>button]:text-brand-muted [&>button]:hover:text-brand-text"
          aria-describedby={undefined}
          data-testid="request-to-sell-modal"
        >
          <DialogTitle className="sr-only">Start Selling</DialogTitle>
          {selectedPost && (
            <div data-testid="request-modal">
              {/* Header */}
              <div className="px-7 pt-7 pb-5 border-b border-white/[0.06]">
                <p className="text-xs font-medium tracking-widest uppercase text-brand-gold mb-2">New Opportunity</p>
                <h2 className="text-xl font-semibold text-brand-text leading-tight">{selectedPost.title}</h2>
                {selectedPost.category && (
                  <span className="inline-block mt-2 text-xs font-medium text-brand-muted bg-white/[0.04] border border-white/[0.06] rounded-md px-2.5 py-1">
                    {selectedPost.category}
                  </span>
                )}
              </div>

              {/* Post Details */}
              <div className="px-7 py-5 border-b border-white/[0.06]">
                {selectedPost.description && (
                  <p className="text-sm text-brand-muted leading-relaxed mb-4">{selectedPost.description}</p>
                )}
                {selectedPost.price != null && (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold text-brand-text">${selectedPost.price.toLocaleString()}</span>
                    <span className="text-xs text-brand-muted font-medium">product value</span>
                  </div>
                )}
              </div>

              {/* Proposed Terms */}
              <div className="px-7 py-5">
                <h3 className="text-sm font-semibold text-brand-text mb-1">Your Proposed Terms</h3>
                <p className="text-xs text-brand-muted mb-5">
                  {selectedPost.defaultTerms
                    ? 'Default terms from this post (editable).'
                    : 'Set your proposed commission to get started.'}
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="commissionPct" className="text-xs font-medium text-brand-muted mb-1.5 block">Commission (%)</Label>
                    <Input
                      id="commissionPct"
                      type="number"
                      min={0}
                      max={60}
                      step={0.5}
                      value={proposedTerms.commissionPct}
                      onChange={(e) => setProposedTerms(prev => ({ ...prev, commissionPct: e.target.value }))}
                      placeholder="0 – 60"
                      className="bg-[#0B0B0D] border-white/[0.08] text-brand-text placeholder:text-zinc-600 focus:border-brand-gold/40 focus:ring-brand-gold/20"
                      data-testid="commission-input"
                    />
                    <p className="text-[11px] text-zinc-600 mt-1.5">Enter a value between 0 and 60%.</p>
                  </div>
                  <div>
                    <Label htmlFor="commissionNotes" className="text-xs font-medium text-brand-muted mb-1.5 block">Notes (optional)</Label>
                    <Textarea
                      id="commissionNotes"
                      rows={2}
                      value={proposedTerms.notes}
                      onChange={(e) => setProposedTerms(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any additional terms or context..."
                      className="bg-[#0B0B0D] border-white/[0.08] text-brand-text placeholder:text-zinc-600 focus:border-brand-gold/40 focus:ring-brand-gold/20 resize-none"
                      data-testid="commission-notes-input"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 px-7 pb-7 pt-2">
                <button
                  type="button"
                  className="flex-1 h-11 rounded-xl text-sm font-medium text-brand-muted border border-white/[0.08] bg-transparent hover:bg-white/[0.03] hover:text-brand-text transition-colors disabled:opacity-40"
                  onClick={() => setRequestModalOpen(false)}
                  disabled={submitting}
                  data-testid="cancel-request-button"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 h-11 rounded-xl text-sm font-bold bg-brand-gold hover:bg-brand-gold600 text-[#0B0B0D] shadow-[0_2px_12px_rgba(212,175,55,0.25)] transition-all disabled:opacity-40"
                  onClick={handleSubmitRequest}
                  disabled={submitting}
                  data-testid="submit-request-button"
                >
                  {submitting ? 'Sending...' : 'Start Selling'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalespersonDashboard;
